'use client';

import { FC, useContext, useState } from 'react';
import {
  Dialog,
  Flex,
  Select,
  ButtonGroup,
  Button,
  Alert,
  SelectItem,
} from '@/_components';
import { AttachToQuestionnaireDialogProps } from './AttachToQuestionnaireDialog.types';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import { attachToQuestionnaireInBulk } from '@/_api/client-documents';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import { DialogFooter } from '@/_components/Dialog/_components/DialogFooter';

export const AttachToQuestionnaireDialog: FC<
  AttachToQuestionnaireDialogProps
> = ({ item, onClose }) => {
  const [questionnaireId, setQuestionnaireId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { selectedCompanyQuestionnaires, selectedCompany } =
    useContext(CompanyContext);
  const { reloadFiles } = useContext(DocumentsContext);
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const questionnaires: Array<SelectItem> = selectedCompanyQuestionnaires
    .filter((questionnaire) => !questionnaire.locked)
    .map((questionnaire) => ({
      name: `${questionnaire.formatted_name} (${questionnaire.tax_year})`,
      value: questionnaire.id.toString(),
    }));

  const onQuestionnaireChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const selectedId = event.target.value;

    setQuestionnaireId(selectedId);
  };

  const onAttach = async () => {
    if (!selectedCompany || !item || !item.extracted_document_id) {
      return;
    }
    setLoading(true);
    const id = parseInt(questionnaireId);
    try {
      const { error } = await attachToQuestionnaireInBulk({
        questionnaireId: id,
        companyId: selectedCompany?.id,
        extractedDocumentIds: item?.extracted_document_id,
        token,
      });

      if (!error) {
        setLoading(false);
        reloadFiles();
        addToast('success', 'Successfully attached');
        onClose();
      }
    } catch (error) {
      addToast('error', 'Error attaching document to questionnaire', { error });
    }
  };

  return (
    <Dialog
      isOpen={!!item}
      title="Attach To Questionnaire"
      onClose={onClose}
      isOverflowing={true}
    >
      <Dialog.Content>
        {questionnaires.length > 0 ? (
          <Flex isColumn={true} gap={8}>
            <Alert type="info">
              You can only attach documents to unlocked questionnaires.
            </Alert>
            <Select
              label="Select Questionnaire"
              items={questionnaires}
              isLabelHidden={true}
              placeholder="Select Questionnaire"
              onChange={onQuestionnaireChange}
              selectedValue={questionnaireId}
              isFullWidth={true}
            />
          </Flex>
        ) : (
          <Alert type="warning">
            All questionnaires are locked, no available questionnaire to attach.
          </Alert>
        )}
      </Dialog.Content>
      <DialogFooter>
        <ButtonGroup align="right">
          <Button
            intent="primary"
            value={loading ? 'Attaching...' : 'Attach'}
            onClick={onAttach}
            isDisabled={loading}
          />
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};
